import {
  ACTIVE,
  BLANK,
  PARTICIPATING,
  SPECTATING,
  useCallMetadataForTopic,
} from 'components/calls/call_metadata/use_call_metadata_for_topic'
import { useCallManager } from 'contexts/call_manager_context'
import React from 'react'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getSelectedDirectChatHandle } from 'reduxes/slices/selected/selectors'

import { ButtonVariantType } from 'primitives/button'

import { CallEndIcon, CallStartIcon, VideoOnIcon } from '../../images'
import { TRANSIENT_STATES } from './call_manager/connection_status_reducer'

type ReturnType =
  | {
      label?: string
      icon?: JSX.Element
      onClick?: any
      variant?: ButtonVariantType
      disabled?: boolean
    }
  | Record<string, never>

export const useCallAction = (
  topicId: number,
  callType: 'audio' | 'video',
): ReturnType => {
  const { POV } = useCallMetadataForTopic(topicId)
  const {
    joinCall,
    joinCallForNewDirectChat,
    leaveCall,
    activeCall: { isOnCall, connectionStatus },
  } = useCallManager()
  const directChatHandle = useSelector(getSelectedDirectChatHandle)

  const joinTheCall = useCallback(() => {
    if (!topicId && !directChatHandle) {
      // addCallError('failed to join call')
      return Promise.reject()
    }

    if (topicId) {
      if (joinCall) {
        return joinCall({ topicId, callType })
      }
    } else {
      if (joinCallForNewDirectChat) {
        return joinCallForNewDirectChat(directChatHandle, { callType })
      }
    }
    return Promise.reject()
  }, [callType, directChatHandle, joinCall, joinCallForNewDirectChat, topicId])

  const leaveThenJoinCall = useCallback(() => {
    if (leaveCall) {
      return leaveCall(undefined, { playLeaveSound: false }).then(() => {
        return joinTheCall()
      })
    }
    return Promise.reject()
  }, [joinTheCall, leaveCall])

  const disabled =
    !!connectionStatus && TRANSIENT_STATES.includes(connectionStatus)
  return useMemo(() => {
    if (!leaveCall || !joinTheCall) {
      return {}
    }

    let attributes
    switch (POV) {
      case PARTICIPATING:
        attributes = {
          label: 'Leave',
          icon: callType === 'audio' ? <CallEndIcon /> : <CallEndIcon />,
          onClick: leaveCall,
          variant: 'danger',
        }
        break
      case SPECTATING:
        attributes = {
          label: 'Join',
          icon: <CallStartIcon />,
          onClick: leaveThenJoinCall,
          variant: 'primary',
        }
        break
      case ACTIVE:
        attributes = {
          label: 'Join',
          icon: <CallStartIcon />,
          onClick: isOnCall ? leaveThenJoinCall : joinTheCall,
          variant: 'primary',
        }
        break
      case BLANK:
        attributes = {
          label: callType === 'audio' ? VOICE_BLANK_LABEL : VIDEO_BLANK_LABEL,
          icon: callType === 'audio' ? <CallStartIcon /> : <VideoOnIcon />,
          onClick: isOnCall ? leaveThenJoinCall : joinTheCall,
          variant: 'secondary',
        }
        break
      default:
        return {}
    }

    return {
      disabled,
      ...attributes,
    }
  }, [
    POV,
    joinTheCall,
    leaveCall,
    leaveThenJoinCall,
    isOnCall,
    disabled,
    callType,
  ])
}

const VOICE_BLANK_LABEL = 'Voice Call'
const VIDEO_BLANK_LABEL = 'Video Call'
export const BLANK_LABELS = [VOICE_BLANK_LABEL, VIDEO_BLANK_LABEL]
